import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, UtensilsCrossed, Users, Wallet, AlertCircle, Store,
  Receipt, FileBarChart, BadgePercent, ClipboardList, Settings, LogOut, Plus, Send, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { formatIQD } from "../lib/format";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/orders", label: "Daily Orders", icon: UtensilsCrossed, testid: "nav-orders" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments" },
  { to: "/balances", label: "Employee Balances", icon: Receipt, testid: "nav-balances" },
  { to: "/outstanding", label: "Outstanding Payments", icon: AlertCircle, testid: "nav-outstanding" },
  { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees" },
  { to: "/restaurants", label: "Restaurants", icon: Store, testid: "nav-restaurants" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/deductions", label: "Salary Deductions", icon: BadgePercent, testid: "nav-deductions" },
  { to: "/audit", label: "Audit Log", icon: ClipboardList, testid: "nav-audit" },
  { to: "/reminder-log", label: "Reminder Log", icon: Send, testid: "nav-reminder-log" },
  { to: "/admins", label: "Admins", icon: ShieldCheck, testid: "nav-admins" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-white dark:bg-slate-900/60 hidden md:flex md:flex-col">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold">
            <UtensilsCrossed size={18} />
          </div>
          <div className="leading-tight">
            <div className="heading font-bold text-slate-900 dark:text-slate-50">MealLedger</div>
            <div className="text-[11px] text-muted-foreground">Food Order Tracker</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                  isActive
                    ? "bg-slate-900 text-white dark:bg-emerald-600"
                    : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`
              }
            >
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold">
              {(user?.email || "A")[0].toUpperCase()}
            </div>
            <div className="flex-1 leading-tight overflow-hidden">
              <div className="text-sm font-medium truncate">{user?.name || "Admin"}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="btn-logout" title="Log out">
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-border bg-white/80 dark:bg-slate-900/60 backdrop-blur-md flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <div className="md:hidden w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center text-white">
              <UtensilsCrossed size={16} />
            </div>
            <div>
              <div className="heading text-lg font-bold text-slate-900 dark:text-slate-50">Daily Food Ledger</div>
              <div className="text-xs text-muted-foreground">All amounts shown in IQD · Iraqi Dinar</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/orders?new=1")} data-testid="btn-quick-create-order" className="bg-emerald-600 hover:bg-emerald-700">
              <Plus size={16} className="mr-1" /> Create Today's Order
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
